import { CONFIG } from '../config';
import type { Bounds, NearMissResult } from '../types';
import type { Bullet } from '../entities/Bullet';
import type { OneWayBullet } from '../entities/OneWayBullet';

// 공통 인터페이스: 총알과 단방향 총알 모두 적용
interface NearMissTarget {
    x: number;
    y: number;
    radius: number;
    isAlive: boolean;
}

/**
 * Near Miss 콤보 시스템 관리
 */
export class ComboManager {
    private combo: number = 0;
    private comboTimer: number = 0;
    private checkedTargets: Set<NearMissTarget> = new Set();
    private nearMissPopups: NearMissPopup[] = [];

    /**
     * Near Miss 체크
     * 플레이어 근처를 스쳐지나간 총알/단방향 총알 감지
     */
    checkNearMiss(playerBounds: Bounds, bullets: Bullet[], oneWayBullets: OneWayBullet[] = []): NearMissResult | null {
        // 총알과 단방향 총알을 합쳐서 체크
        const allTargets: NearMissTarget[] = [...bullets, ...oneWayBullets];

        const nearMissDistance = CONFIG.COMBO.NEAR_MISS_DISTANCE;
        const playerRadius = playerBounds.radius;

        for (const target of allTargets) {
            // 이미 체크한 타겟은 스킵
            if (this.checkedTargets.has(target)) continue;
            if (!target.isAlive) continue;

            // 플레이어와 타겟 사이 거리
            const dx = target.x - playerBounds.x;
            const dy = target.y - playerBounds.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            // 충돌하지 않았지만 근접한 경우 (Near Miss)
            const collisionDistance = playerRadius + target.radius;
            const nearMissThreshold = collisionDistance + nearMissDistance;

            if (distance > collisionDistance && distance <= nearMissThreshold) {
                // Near Miss 발생!
                this.checkedTargets.add(target);
                this.combo++;
                this.comboTimer = CONFIG.COMBO.COMBO_TIMEOUT;

                const points = this.calculatePoints();
                const isPerfect = this.combo >= CONFIG.COMBO.PERFECT_THRESHOLD &&
                                  this.combo % CONFIG.COMBO.PERFECT_THRESHOLD === 0;

                // 팝업 추가
                this.nearMissPopups.push({
                    x: target.x,
                    y: target.y,
                    points,
                    combo: this.combo,
                    isPerfect,
                    timer: 1.0, // 1초간 표시
                    opacity: 1.0
                });

                return {
                    x: target.x,
                    y: target.y,
                    points,
                    combo: this.combo,
                    isPerfect
                };
            }
        }

        return null;
    }

    /**
     * 점수 계산
     */
    private calculatePoints(): number {
        const basePoints = CONFIG.COMBO.NEAR_MISS_POINTS;
        const multiplier = this.getMultiplier();
        return Math.floor(basePoints * multiplier);
    }

    /**
     * 콤보 배율 반환
     */
    getMultiplier(): number {
        const multipliers = CONFIG.COMBO.MULTIPLIERS as Record<number, number>;
        let mult = 1.0;

        // 콤보 수에 따른 배율 적용
        for (const [threshold, multiplier] of Object.entries(multipliers)) {
            if (this.combo >= parseInt(threshold)) {
                mult = multiplier;
            }
        }

        return mult;
    }

    /**
     * 업데이트
     */
    update(deltaTime: number): void {
        // 콤보 타이머 감소
        if (this.comboTimer > 0) {
            this.comboTimer -= deltaTime;
            if (this.comboTimer <= 0) {
                // 콤보 리셋
                this.combo = 0;
                this.checkedTargets.clear();
            }
        }

        // 팝업 업데이트
        for (let i = this.nearMissPopups.length - 1; i >= 0; i--) {
            const popup = this.nearMissPopups[i];
            popup.timer -= deltaTime;
            popup.opacity = Math.max(0, popup.timer);
            popup.y -= 50 * deltaTime; // 위로 떠오름

            if (popup.timer <= 0) {
                this.nearMissPopups.splice(i, 1);
            }
        }

        // 죽은 타겟은 체크 목록에서 제거
        for (const target of this.checkedTargets) {
            if (!target.isAlive) {
                this.checkedTargets.delete(target);
            }
        }
    }

    /**
     * 현재 콤보 반환
     */
    getCombo(): number {
        return this.combo;
    }

    /**
     * 콤보 타이머 비율 (UI 표시용)
     */
    getComboTimerRatio(): number {
        if (this.combo === 0) return 0;
        return this.comboTimer / CONFIG.COMBO.COMBO_TIMEOUT;
    }

    /**
     * 팝업 목록 반환
     */
    getPopups(): NearMissPopup[] {
        return this.nearMissPopups;
    }

    /**
     * 리셋
     */
    reset(): void {
        this.combo = 0;
        this.comboTimer = 0;
        this.checkedTargets.clear();
        this.nearMissPopups = [];
    }
}

interface NearMissPopup {
    x: number;
    y: number;
    points: number;
    combo: number;
    isPerfect: boolean;
    timer: number;
    opacity: number;
}
